'use client'

import {
  IconChevronRight,
  IconFuelefficiencyFill,
  IconGasstationFill,
  IconProfileFill,
  IconStarFill,
} from '@macarongfactory/web-icons'
import { Divider, Text } from '@macarongfactory/web-ui'
import Link from 'next/link'
import { useState } from 'react'

import { buildModoocarDetailPath } from '@/app/(makers)/modoocar/rank/_features/rank'

import RankBadge from './RankBadge'
import {
  buildRankItemClickLabel,
  HOME_TRACK_CODE,
  HOME_TRACK_TYPE,
  useHomeTracking,
} from '../_features/modoocar-home'

import type {
  ICarRankingItem,
  TRankingCriteria,
} from '../_features/modoocar-home'

const STAT_ICON_SIZE_PX = 16
const MOBILE_CHEVRON_SIZE_PX = 16

const CarThumb = ({
  item,
  className,
}: {
  item: ICarRankingItem
  className?: string
}) => {
  const [srcIndex, setSrcIndex] = useState(0)
  const src = item.imageUrls[srcIndex]

  if (!src) {
    return (
      <div className={`flex shrink-0 items-center justify-center ${className}`}>
        <Text
          variant='caption-1-medium'
          color='text-foreground-neutral-assistive'
        >
          {item.carName}
        </Text>
      </div>
    )
  }

  return (
    <div className={`shrink-0 overflow-hidden ${className}`}>
      <img
        key={src}
        src={src}
        alt={item.carName}
        className='size-full object-cover'
        onError={() => setSrcIndex(prev => prev + 1)}
      />
    </div>
  )
}

const MainStat = ({
  item,
  criteria,
}: {
  item: ICarRankingItem
  criteria: TRankingCriteria
}) => {
  if (criteria === 'fuelEfficiency') {
    return (
      <span className='flex items-center gap-dimension-x0-5'>
        <span className='inline-flex text-foreground-success-strong'>
          <IconFuelefficiencyFill
            width={STAT_ICON_SIZE_PX}
            height={STAT_ICON_SIZE_PX}
          />
        </span>
        <Text variant='title-2-semibold' color='text-foreground-neutral-strong'>
          {item.efficiency}km/L
        </Text>
      </span>
    )
  }

  if (criteria === 'fuelCost') {
    return (
      <span className='flex items-center gap-dimension-x0-5'>
        <span className='inline-flex text-foreground-brand-strong'>
          <IconGasstationFill
            width={STAT_ICON_SIZE_PX}
            height={STAT_ICON_SIZE_PX}
          />
        </span>
        <Text variant='title-2-semibold' color='text-foreground-neutral-strong'>
          약 {item.fuelCost}만원
        </Text>
      </span>
    )
  }

  return (
    <span className='flex items-center gap-dimension-x0-5'>
      <span className='inline-flex text-light-amber-400'>
        <IconStarFill width={STAT_ICON_SIZE_PX} height={STAT_ICON_SIZE_PX} />
      </span>
      <Text variant='title-2-semibold' color='text-foreground-neutral-strong'>
        {item.score.toFixed(2)}점
      </Text>
    </span>
  )
}

const StatRow = ({
  item,
  criteria,
  gap,
}: {
  item: ICarRankingItem
  criteria: TRankingCriteria
  gap: string
}) => (
  <div className={`flex items-center ${gap}`}>
    <MainStat item={item} criteria={criteria} />
    <Divider.Vertical height='x4' />
    <span className='flex items-center gap-dimension-x0-5'>
      <span className='inline-flex text-foreground-neutral-assistive'>
        <IconProfileFill width={STAT_ICON_SIZE_PX} height={STAT_ICON_SIZE_PX} />
      </span>
      <Text variant='title-2-semibold' color='text-foreground-neutral-normal'>
        {item.ownerCount.toLocaleString()}명
      </Text>
    </span>
  </div>
)

const buildDetailHref = (item: ICarRankingItem) =>
  buildModoocarDetailPath({
    vehicleType: item.vehicleType === '모터사이클' ? 'MOTORCYCLE' : 'CAR',
    brandName: item.brand,
    modelName: item.model,
    fuelType: item.fuelType,
  })

const RankingListItem = ({
  item,
  criteria,
  isFirst,
}: {
  item: ICarRankingItem
  criteria: TRankingCriteria
  isFirst?: boolean
}) => {
  const { itemClick } = useHomeTracking()

  const handleItemClick = () =>
    itemClick(
      HOME_TRACK_CODE.HOME_RANK,
      HOME_TRACK_TYPE.LIST_ITEM,
      buildRankItemClickLabel(item.carName),
      {
        rank: item.rank,
        target: `${item.brand} / ${item.model}`,
      }
    )

  const isFuelCost = criteria === 'fuelCost'

  // [주의]: JS 미디어쿼리로 나누면 SSR 첫 렌더가 늘 데스크탑이라 모바일에서 하이드레이션 불일치와 레이아웃 밀림(CLS)이 생깁니다.
  // 그래서 두 마크업을 모두 그리고 CSS로 전환합니다.
  return (
    <>
      <div className='hidden max-[639px]:block'>
        {!isFirst && <Divider.Horizontal />}
        <Link
          href={buildDetailHref(item)}
          className='flex flex-col gap-dimension-x1 bg-background-surface-default px-dimension-x4 pb-dimension-x4'
          onClick={handleItemClick}
        >
          <div className='flex items-start gap-dimension-x3'>
            <RankBadge
              rank={item.rank}
              labelPosition={isFuelCost ? 'mobileFuel' : 'mobileStat'}
            />
            <div className='flex min-w-0 flex-1 flex-col gap-dimension-x2 pt-dimension-x3'>
              <div className='flex items-end gap-dimension-x2'>
                {isFuelCost ? (
                  <div className='flex min-w-0 flex-1 flex-col gap-dimension-x1'>
                    <p className='truncate text-title-1-bold text-foreground-neutral-strong'>
                      {item.carName}
                    </p>
                    <Text
                      variant='caption-1-medium'
                      color='text-foreground-neutral-normal'
                    >
                      {item.fuelTypeName}
                    </Text>
                  </div>
                ) : (
                  <p className='min-w-0 flex-1 truncate text-title-1-bold text-foreground-neutral-strong'>
                    {item.carName}
                  </p>
                )}
                <div className='flex shrink-0 items-center gap-dimension-x2'>
                  <CarThumb item={item} className='h-[40.5px] w-[72px]' />
                  <span className='shrink-0 text-foreground-neutral-assistive'>
                    <IconChevronRight
                      width={MOBILE_CHEVRON_SIZE_PX}
                      height={MOBILE_CHEVRON_SIZE_PX}
                    />
                  </span>
                </div>
              </div>
              <StatRow item={item} criteria={criteria} gap='gap-dimension-x2' />
            </div>
          </div>
        </Link>
      </div>

      <Link
        href={buildDetailHref(item)}
        className='flex items-end justify-between overflow-hidden rounded-dimension-x4 bg-background-surface-default pb-dimension-x4 pl-dimension-x6 pr-dimension-x8 shadow-weak max-[639px]:hidden'
        onClick={handleItemClick}
      >
        <div className='flex min-w-0 flex-1 items-start gap-dimension-x4'>
          <RankBadge
            rank={item.rank}
            labelPosition={isFuelCost ? 'desktopFuel' : 'desktopStat'}
          />
          <div className='flex min-w-0 flex-1 flex-col gap-dimension-x1'>
            <div className='pt-dimension-x3-5'>
              {isFuelCost ? (
                <div className='flex min-w-0 items-center gap-dimension-x1'>
                  <p className='min-w-0 truncate text-title-1-bold text-foreground-neutral-strong'>
                    {item.carName}
                  </p>
                  <Text
                    variant='caption-1-medium'
                    color='text-foreground-neutral-normal'
                    className='shrink-0'
                  >
                    {item.fuelTypeName}
                  </Text>
                </div>
              ) : (
                <p className='truncate text-title-1-bold text-foreground-neutral-strong'>
                  {item.carName}
                </p>
              )}
            </div>
            <StatRow item={item} criteria={criteria} gap='gap-dimension-x4' />
          </div>
        </div>
        <CarThumb item={item} className='h-[56.25px] w-[100px]' />
      </Link>
    </>
  )
}

export default RankingListItem
